// src/pages/MyFavorites.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, MapPin, ArrowRight, Trash2, Hotel, Music, Compass, Newspaper, Store, ExternalLink, RefreshCw } from 'lucide-react';
import { FaCheckCircle } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useFavorites } from '../hooks/useFavorites';

const MyFavorites = () => {
  const { favorites, loading, toggleFavorite, userId } = useFavorites();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'favorites' | 'feed'>('favorites');
  const [filter, setFilter] = useState<'all' | 'hotel' | 'activity'>('all');
  const [networkFeed, setNetworkFeed] = useState<any[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) fetchNetworkFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchNetworkFeed = async () => {
    setLoadingFeed(true);
    setFeedError(null);
    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          businesses (
            id, name, image_url, category, plan_type, region
          )
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setNetworkFeed((data || []).filter((post: any) => post.businesses));
    } catch (error) {
      console.error('Erreur chargement feed:', error);
      setFeedError("Impossible de charger le fil d'actualité pour le moment.");
    } finally {
      setLoadingFeed(false);
    }
  };

  const filteredFavorites = useMemo(() => {
    return favorites.filter((fav: any) => filter === 'all' || fav.type === filter);
  }, [favorites, filter]);

  const goToFavorite = (fav: any) => {
    navigate(`/explore/${fav.type}/${fav.item_id}`, { state: { item: fav.data } });
  };

  if (!userId && !loading) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans">
        <Header />
        <main className="pt-32 pb-24 px-4">
          <div className="max-w-xl mx-auto bg-white rounded-3xl border border-slate-100 p-10 text-center shadow-sm">
            <Heart className="mx-auto text-slate-300 mb-5" size={54} />
            <h1 className="text-3xl font-black text-slate-900 mb-3">Connectez-vous pour voir vos favoris</h1>
            <p className="text-slate-500 mb-8">Vos expériences sauvegardées sont liées à votre compte Forfeo.</p>
            <Link to="/login?redirect=/mes-favoris" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-black">
              Se connecter <ArrowRight size={16} />
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24">
      <Header />
      <main className="pt-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-600 border border-pink-100 rounded-full px-4 py-2 text-xs font-black uppercase tracking-widest mb-4">
                <Heart size={14} /> Carnet de voyage
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-slate-900">Mes favoris</h1>
              <p className="text-slate-500 mt-2">Retrouvez vos hôtels, activités et inspirations sauvegardés.</p>
            </div>
            <div className="flex gap-2 rounded-2xl bg-white border border-slate-200 p-1">
              <button onClick={() => setActiveTab('favorites')} className={`px-4 py-2 rounded-xl text-sm font-black transition ${activeTab === 'favorites' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                Favoris
              </button>
              <button onClick={() => setActiveTab('feed')} className={`px-4 py-2 rounded-xl text-sm font-black transition ${activeTab === 'feed' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                Réseau local
              </button>
            </div>
          </div>

          {activeTab === 'favorites' && (
            <section>
              {favorites.length > 0 && (
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
                  <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-full text-xs font-bold transition whitespace-nowrap ${filter === 'all' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>
                    Tout ({favorites.length})
                  </button>
                  <button onClick={() => setFilter('hotel')} className={`px-4 py-2 rounded-full text-xs font-bold transition whitespace-nowrap flex items-center gap-2 ${filter === 'hotel' ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>
                    <Hotel size={14} /> Hôtels
                  </button>
                  <button onClick={() => setFilter('activity')} className={`px-4 py-2 rounded-full text-xs font-bold transition whitespace-nowrap flex items-center gap-2 ${filter === 'activity' ? 'bg-purple-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>
                    <Music size={14} /> Activités
                  </button>
                </div>
              )}

              {loading ? (
                <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 border-4 border-slate-200 border-t-pink-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-slate-500 font-bold">Chargement des favoris...</p>
                </div>
              ) : filteredFavorites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {filteredFavorites.map((fav: any) => (
                    <div key={fav.id} onClick={() => goToFavorite(fav)} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all cursor-pointer group relative hover:-translate-y-1">
                      <div className="h-48 bg-slate-200 relative overflow-hidden">
                        <img src={fav.data?.image || 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa'} alt={fav.data?.name || 'Favori Forfeo'} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        <div className={`absolute top-3 left-3 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm backdrop-blur-md ${fav.type === 'hotel' ? 'bg-blue-500/90 text-white' : 'bg-purple-500/90 text-white'}`}>
                          {fav.type === 'hotel' ? <Hotel size={10} /> : <Music size={10} />} {fav.type === 'hotel' ? 'Hébergement' : 'Activité'}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); toggleFavorite({ hotelId: fav.item_id, id: fav.item_id }, fav.type); }} className="absolute top-3 right-3 p-2 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-red-500 transition shadow-sm">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="p-5">
                        <h3 className="font-bold text-slate-900 text-sm line-clamp-1 mb-1">{fav.data?.name || fav.name || 'Favori Forfeo'}</h3>
                        <p className="text-xs text-slate-500 mb-4 flex items-center gap-1 truncate"><MapPin size={12} /> {fav.data?.location || 'Québec, CA'}</p>
                        <div className="flex justify-between items-center pt-3 border-t border-slate-50">
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 rounded font-bold">Sauvegardé</span>
                          <div className="text-xs font-bold text-blue-600 group-hover:underline flex items-center gap-1">Voir <ArrowRight size={12} /></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 px-6 bg-white rounded-3xl border-2 border-dashed border-slate-200">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300"><Compass size={40} /></div>
                  <h3 className="text-xl font-black text-slate-800 mb-2">Votre liste est vide</h3>
                  <p className="text-slate-500 text-sm mb-8">Commencez à explorer pour remplir votre carnet de voyage.</p>
                  <button onClick={() => navigate('/explorer-plus')} className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition">Explorer maintenant</button>
                </div>
              )}
            </section>
          )}

          {activeTab === 'feed' && (
            <section className="max-w-3xl mx-auto">
              <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 mb-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="bg-white p-2 rounded-full text-blue-600"><Newspaper size={20} /></div>
                  <div>
                    <h3 className="font-bold text-blue-900 text-sm">Quoi de neuf au Québec ?</h3>
                    <p className="text-xs text-blue-700">Les dernières actualités des entreprises locales.</p>
                  </div>
                </div>
                <button onClick={fetchNetworkFeed} className="p-2 rounded-full bg-white text-blue-600 hover:bg-blue-100"><RefreshCw size={16} /></button>
              </div>

              {feedError && <div className="mb-5 p-4 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm font-semibold">{feedError}</div>}

              {loadingFeed ? (
                <div className="text-center py-10 text-slate-400">Chargement des actualités...</div>
              ) : networkFeed.length > 0 ? (
                <div className="space-y-6">
                  {networkFeed.map((post) => (
                    <div key={post.id} className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 flex items-center justify-between">
                        <Link to={`/place/${post.businesses.id}`} className="flex items-center gap-3 group">
                          <img src={post.businesses.image_url || 'https://via.placeholder.com/50'} className="w-12 h-12 rounded-full object-cover border border-slate-100" alt={post.businesses.name} />
                          <div>
                            <h4 className="font-bold text-slate-900 group-hover:text-blue-600 transition flex items-center gap-1">
                              {post.businesses.name}
                              {post.businesses.plan_type === 'vedette' && <FaCheckCircle className="text-blue-500 text-xs" />}
                            </h4>
                            <p className="text-[10px] text-slate-400 font-bold uppercase">{post.businesses.category} • {post.businesses.region}</p>
                          </div>
                        </Link>
                        <Link to={`/place/${post.businesses.id}`} className="p-2 bg-slate-50 rounded-full hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition">
                          <ExternalLink size={18} />
                        </Link>
                      </div>
                      <div className="px-4 pb-3">
                        <p className="text-slate-700 whitespace-pre-line text-sm leading-relaxed">{post.content}</p>
                      </div>
                      {post.image_url && (
                        <div className="w-full bg-black">
                          {post.image_url.includes('.mp4') ? (
                            <video controls className="w-full h-auto max-h-[400px]"><source src={post.image_url} type="video/mp4" /></video>
                          ) : (
                            <img src={post.image_url} className="w-full h-auto object-cover max-h-[400px]" alt="Post media" />
                          )}
                        </div>
                      )}
                      <div className="p-4 border-t border-slate-50 flex justify-between items-center bg-slate-50/50">
                        <span className="text-[10px] text-slate-400 font-bold uppercase">{new Date(post.created_at).toLocaleDateString()}</span>
                        <Link to={`/place/${post.businesses.id}`} className="text-xs font-black text-blue-600 uppercase tracking-widest hover:underline">Visiter la fiche</Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 text-slate-400">
                  <Store size={48} className="mx-auto mb-4 opacity-20" />
                  <p>Aucune actualité récente sur le réseau.</p>
                </div>
              )}
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MyFavorites;
